import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Input, Label, Button } from 'reactstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { useExpenseClaimUtilCtx } from '../../../../../pages/ExpenseClaims/context/ExpenseClaimUtilProvider';
import { FileUploadDropzone } from '../../../../Common';
import {
  validationSchema,
  initialValues,
  CLAIM_ATTACHMENT_TYPES,
} from './constants';
import { CLAIM_FORM_TYPES } from '../../../../../pages/ExpenseClaims/Employee/EmployeeExpenseClaim/constants';

import './UploadForm.scss';

export default function UploadForm({ formType, claimNo, finCoCd }) {
  const queryClient = useQueryClient();
  const { uploadAttachmentMutation } = useExpenseClaimUtilCtx();
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isValid },
    setError,
    clearErrors,
  } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const onSubmit = async values => {
    if (!values.file || values.file.length === 0) {
      toast.error('Please select a claim evidence file to upload.', {
        className: 'toast-error',
      });
      return;
    }
    const file = values.file[0];
    const formData = new FormData();
    formData.append('docType', CLAIM_ATTACHMENT_TYPES.DOC_TYPE_EXPENSE_CLAIM);
    formData.append('claimNo', claimNo || '');
    formData.append('docFileName', file?.name);
    formData.append('docTypeCode', CLAIM_ATTACHMENT_TYPES.CLAIM_EVIDENCE);
    formData.append('comments', values?.comments || '');
    formData.append('docIndex', 0);
    formData.append('finCoCd', finCoCd || '');
    // Upload File array
    values.file.forEach(singleFile => {
      formData.append('file', singleFile);
    });
    await uploadAttachmentMutation.mutateAsync(formData);
    toast.success('Attachment uploaded successfully.', {
      className: 'toast-success',
    });
    reset();
    queryClient.invalidateQueries({
      queryKey: ['expense-claim-attachments'],
    });
  };

  return (
    <div>
      <div className="d-flex gap-2 align-items-start mb-1">
        <div className="flex-grow-1">
          <Label>
            Upload Attachments<span className="text-danger">*</span>
          </Label>
          <Controller
            name="file"
            control={control}
            render={({ field }) => (
              <FileUploadDropzone
                error={errors.file}
                onDrop={async acceptedFiles => {
                  try {
                    await validationSchema.fields.file.validate(acceptedFiles);
                    clearErrors('file');
                    setValue('file', acceptedFiles, {
                      shouldValidate: true,
                    });
                  } catch (err) {
                    setError('file', {
                      type: 'manual',
                      message: err.message,
                    });
                  }
                }}
                selectedFiles={field.value}
                className={errors.file ? 'is-invalid' : ''}
                handleDeleteFile={file => {
                  field.onChange(field.value.filter(f => f.name !== file.name));
                }}
                showFileList={false}
                showName
                disabled={formType === CLAIM_FORM_TYPES.VIEW}
                multiple
                placeholder="Browse Files/ Drag and Drop Files"
              />
            )}
          />
          {errors.file && (
            <div className="invalid-feedback">{errors.file.message}</div>
          )}
        </div>
      </div>
      <div>
        <Label>Comments</Label>
        <Controller
          name="comments"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="textarea"
              className={`form-control external-filter-input ${
                errors.comments ? 'is-invalid' : ''
              }`}
              placeholder="Enter Comments"
              disabled={formType === CLAIM_FORM_TYPES.VIEW}
            />
          )}
        />
        {errors.comments && (
          <div className="invalid-feedback">{errors.comments.message}</div>
        )}
      </div>
      <div className="d-flex justify-content-center gap-2 mt-3">
        {formType !== CLAIM_FORM_TYPES.VIEW && (
          <>
            <Button
              className="upload-form-cancel-button"
              onClick={() => reset()}
            >
              Cancel
            </Button>
            <Button
              className="upload-form-upload-button"
              disabled={!isValid || uploadAttachmentMutation.isPending}
              onClick={handleSubmit(onSubmit)}
            >
              <span>
                {uploadAttachmentMutation.isPending ? 'Uploading...' : 'Upload'}
              </span>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
